import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Server,
  Database,
  Wifi,
  AlertCircle,
  CheckCircle,
  Clock,
  HardDrive,
  Cpu,
  Activity,
  RefreshCw,
  Shield,
  Zap
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area } from 'recharts';
import { backendClient } from '@/lib/backendClient';
import { useToast } from '@/hooks/use-toast';
import { useMonitoringDashboard, useDetailedHealth, useStorageMetrics } from '@/hooks/useMonitoringMetrics';
import QueueDashboard from '@/components/admin/QueueDashboard';

interface SystemStatus {
  status: 'healthy' | 'warning' | 'critical';
  message: string;
  lastChecked: string;
}

interface PerformanceMetrics {
  responseTime: number;
  uptime: number;
  errorRate: number;
  throughput: number;
}

interface SystemHealthData {
  // System status
  database: SystemStatus;
  api: SystemStatus;
  storage: SystemStatus;
  authentication: SystemStatus;
  
  // Performance metrics
  performance: PerformanceMetrics;
  
  // Resource usage
  storageUsage: { used: number; total: number; percentage: number };
  
  // Historical performance data
  performanceHistory: Array<{
    time: string;
    responseTime: number;
    errorRate: number;
    requests: number;
  }>;
  
  // Error logs
  recentErrors: Array<{
    timestamp: string;
    type: string;
    message: string;
    severity: 'low' | 'medium' | 'high';
  }>;
  
  // Security metrics
  securityMetrics: {
    failedLogins: number;
    suspiciousActivity: number;
    lastSecurityScan: string;
  };
}

const SystemHealth: React.FC = () => {
  const [healthData, setHealthData] = useState<SystemHealthData | null>(null);
  const { toast } = useToast();

  // Use real backend monitoring hooks
  const { data: dashboardData, isLoading: dashboardLoading, refetch: refetchDashboard } = useMonitoringDashboard();
  const { data: detailedHealth, isLoading: healthLoading, refetch: refetchHealth } = useDetailedHealth();
  const { data: storageData, isLoading: storageLoading, refetch: refetchStorage } = useStorageMetrics();

  const loading = dashboardLoading || healthLoading || storageLoading;

  // Update health data when backend data changes
  useEffect(() => {
    if (dashboardData && detailedHealth) {
      // Generate performance history from time_series data in performance_history
      const performanceHistory = dashboardData.performance_history?.time_series?.map((stat: any) => ({
        time: new Date(stat.timestamp).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
        responseTime: stat.processing_time || 0,
        errorRate: stat.failure_rate || 0,
        requests: stat.jobs_per_minute || 0
      })) || [];

      // Use real alerts as error logs
      const recentErrors = (dashboardData.alerts || []).map((alert: any) => ({
        timestamp: alert.timestamp || new Date().toISOString(),
        type: alert.type || 'UNKNOWN',
        message: alert.message || 'No message',
        severity: alert.severity || 'low'
      }));

      // Use real storage metrics
      const storageUsage = storageData ? {
        used: storageData.total_size_mb || 0,
        total: storageData.storage_limit_mb || 0,
        percentage: storageData.usage_percentage || 0
      } : {
        used: 0,
        total: 0,
        percentage: 0
      };

      // Determine storage status from storage data
      const storageStatus = storageData?.status || 'healthy';
      const storageMessage = storageData ?
        `${storageData.total_files} files, ${storageData.total_size_mb}MB used` :
        'Storage systems operational';

      setHealthData({
        database: detailedHealth.database || {
          status: 'healthy',
          message: 'Database connection normal',
          lastChecked: new Date().toISOString()
        },
        api: detailedHealth.api || {
          status: 'healthy',
          message: 'API performance normal',
          lastChecked: new Date().toISOString()
        },
        storage: {
          status: storageStatus,
          message: storageMessage,
          lastChecked: storageData?.timestamp || new Date().toISOString()
        },
        authentication: detailedHealth.authentication || {
          status: 'healthy',
          message: 'Authentication service running normally',
          lastChecked: new Date().toISOString()
        },

        performance: {
          responseTime: dashboardData.performance?.avg_response_time_ms || 0,
          uptime: detailedHealth.uptime_percentage || 99.0,
          errorRate: (dashboardData.performance?.error_rate || 0) * 100, // Convert to percentage
          throughput: dashboardData.current_metrics?.throughput_per_minute || 0
        },

        storageUsage,

        performanceHistory,
        recentErrors,

        securityMetrics: {
          failedLogins: 0, // Not tracking security events yet
          suspiciousActivity: 0,
          lastSecurityScan: new Date().toISOString()
        }
      });
    }
  }, [dashboardData, detailedHealth, storageData]);

  const refreshHealthData = async () => {
    await Promise.all([refetchDashboard(), refetchHealth(), refetchStorage()]);
    toast({
      title: "System Health Refreshed",
      description: "Latest system status has been updated.",
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy': return 'text-green-600 bg-green-100 border-green-200';
      case 'warning': return 'text-yellow-600 bg-yellow-100 border-yellow-200';
      case 'critical': return 'text-red-600 bg-red-100 border-red-200';
      default: return 'text-gray-600 bg-gray-100 border-gray-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'healthy': return <CheckCircle className="h-4 w-4" />;
      case 'warning': return <AlertCircle className="h-4 w-4" />;
      case 'critical': return <AlertCircle className="h-4 w-4" />;
      default: return <Clock className="h-4 w-4" />;
    }
  };

  if (loading || !healthData) {
    return (
      <div className="space-y-6">
        {Array.from({ length: 3 }).map((_, i) => (
          <Card key={i}>
            <CardContent className="p-6">
              <div className="flex items-center justify-center h-32">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* System Status Overview */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">System Health Monitor</h2>
        <Button
          onClick={refreshHealthData}
          disabled={loading}
          variant="outline"
          size="sm"
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* System Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <Database className="h-4 w-4" />
                  Database
                </p>
                <Badge className={`mt-2 ${getStatusColor(healthData.database.status)}`}>
                  {getStatusIcon(healthData.database.status)}
                  <span className="ml-1">{healthData.database.status}</span>
                </Badge>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              {healthData.database.message}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <Server className="h-4 w-4" />
                  API Service
                </p>
                <Badge className={`mt-2 ${getStatusColor(healthData.api.status)}`}>
                  {getStatusIcon(healthData.api.status)}
                  <span className="ml-1">{healthData.api.status}</span>
                </Badge>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              {healthData.api.message}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <HardDrive className="h-4 w-4" />
                  Storage
                </p>
                <Badge className={`mt-2 ${getStatusColor(healthData.storage.status)}`}>
                  {getStatusIcon(healthData.storage.status)}
                  <span className="ml-1">{healthData.storage.status}</span>
                </Badge>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              {healthData.storageUsage.total > 0
                ? `${healthData.storageUsage.percentage}% used (${healthData.storageUsage.used}MB / ${healthData.storageUsage.total}MB)`
                : `${healthData.storageUsage.used}MB used (unlimited)`
              }
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <Shield className="h-4 w-4" />
                  Security
                </p>
                <Badge className="mt-2 text-green-600 bg-green-100 border-green-200">
                  {getStatusIcon('healthy')}
                  <span className="ml-1">Secure</span>
                </Badge>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              {healthData.securityMetrics.failedLogins} failed logins today
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Performance Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-r from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-blue-600">Response Time</p>
                <p className="text-3xl font-bold text-blue-900">{healthData.performance.responseTime}ms</p>
              </div>
              <Zap className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-r from-green-50 to-green-100 border-green-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-green-600">Uptime</p>
                <p className="text-3xl font-bold text-green-900">{healthData.performance.uptime}%</p>
              </div>
              <Activity className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-r from-orange-50 to-orange-100 border-orange-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-orange-600">Error Rate</p>
                <p className="text-3xl font-bold text-orange-900">{healthData.performance.errorRate}%</p>
              </div>
              <AlertCircle className="h-8 w-8 text-orange-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-r from-purple-50 to-purple-100 border-purple-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-purple-600">Throughput</p>
                <p className="text-3xl font-bold text-purple-900">{healthData.performance.throughput}</p>
                <p className="text-xs text-purple-600 mt-1">req/min</p>
              </div>
              <Cpu className="h-8 w-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Performance Charts and Recent Errors */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5" />
              Performance History (24h)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {healthData.performanceHistory && healthData.performanceHistory.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={healthData.performanceHistory}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="time" />
                  <YAxis domain={[0, 'auto']} />
                  <Tooltip
                    contentStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.95)', border: '1px solid #ccc' }}
                  />
                  <Line
                    type="monotone"
                    dataKey="responseTime"
                    stroke="#8884d8"
                    strokeWidth={2}
                    name="Response Time (ms)"
                    dot={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="requests"
                    stroke="#82ca9d"
                    strokeWidth={2}
                    name="Jobs/Min"
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[300px] text-muted-foreground">
                <div className="text-center space-y-2">
                  <Activity className="w-12 h-12 mx-auto opacity-50" />
                  <p className="font-medium">No performance data available</p>
                  <p className="text-sm">Metrics collection initializing...</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5" />
              Recent Issues
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {healthData.recentErrors.length === 0 ? (
                <div className="text-center py-8">
                  <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
                  <p className="text-lg font-medium text-green-700">No Recent Issues</p>
                  <p className="text-sm text-muted-foreground">System running smoothly</p>
                </div>
              ) : (
                healthData.recentErrors.map((error, index) => (
                  <Alert key={index} className={`${
                    error.severity === 'high' ? 'border-red-200 bg-red-50' :
                    error.severity === 'medium' ? 'border-yellow-200 bg-yellow-50' :
                    'border-blue-200 bg-blue-50'
                  }`}>
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">{error.type}</p>
                          <p className="text-sm">{error.message}</p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {new Date(error.timestamp).toLocaleString()}
                          </p>
                        </div>
                        <Badge variant={
                          error.severity === 'high' ? 'destructive' :
                          error.severity === 'medium' ? 'default' : 'secondary'
                        }>
                          {error.severity}
                        </Badge>
                      </div>
                    </AlertDescription>
                  </Alert>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Security Overview */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Security Overview
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center p-4 rounded-lg bg-red-50">
              <AlertCircle className="h-8 w-8 text-red-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-red-900">{healthData.securityMetrics.failedLogins}</p>
              <p className="text-sm text-red-600">Failed Login Attempts (24h)</p>
            </div>
            <div className="text-center p-4 rounded-lg bg-yellow-50">
              <AlertCircle className="h-8 w-8 text-yellow-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-yellow-900">{healthData.securityMetrics.suspiciousActivity}</p>
              <p className="text-sm text-yellow-600">Suspicious Activities</p>
            </div>
            <div className="text-center p-4 rounded-lg bg-green-50">
              <CheckCircle className="h-8 w-8 text-green-500 mx-auto mb-2" />
              <p className="text-sm font-bold text-green-900">Last Security Scan</p>
              <p className="text-sm text-green-600">
                {new Date(healthData.securityMetrics.lastSecurityScan).toLocaleDateString()}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Queue System Dashboard */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5" />
            Queue System Monitor
          </CardTitle>
        </CardHeader>
        <CardContent>
          <QueueDashboard />
        </CardContent>
      </Card>
    </div>
  );
};

export default SystemHealth;